import { useState } from "react";
import type { HrClinicCertificate } from "@dentacore/shared-types";

import { ConfirmDialog } from "../ui/confirm-dialog";
import { Modal } from "../ui/modal";
import styles from "./staff.module.css";
import { shortDate } from "./team";
import own from "./team.module.css";
import { RequiredMark } from "../ui/required-mark";

/** The certificate fields a manager types (HR-D5): a name, the tick, an optional expiry. */
function CertificateFields({
  name,
  neededToTreat,
  expiresOn,
  busy,
  onName,
  onNeeded,
  onExpires,
}: {
  name: string;
  neededToTreat: boolean;
  expiresOn: string;
  busy: boolean;
  onName: (value: string) => void;
  onNeeded: (value: boolean) => void;
  onExpires: (value: string) => void;
}): React.JSX.Element {
  return (
    <>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          Certificate
          <RequiredMark />
        </span>
        <input
          type="text"
          className={styles.input}
          maxLength={120}
          value={name}
          disabled={busy}
          placeholder="What it is, as it is written on it"
          onChange={(event) => onName(event.target.value)}
          data-testid="certificate-name"
        />
      </label>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Expires</span>
        <input
          type="date"
          className={styles.input}
          value={expiresOn}
          disabled={busy}
          onChange={(event) => onExpires(event.target.value)}
          data-testid="certificate-expiry"
        />
        <p className={own.fieldNote}>Leave it empty for one that does not run out.</p>
      </label>
      <label className={own.check}>
        <input
          type="checkbox"
          checked={neededToTreat}
          disabled={busy}
          onChange={(event) => onNeeded(event.target.checked)}
          data-testid="certificate-needed"
        />
        <span>
          Needed to treat
          <span className={own.sub}>
            {" "}
            — when it lapses they are flagged, and can still be booked (HR-D3)
          </span>
        </span>
      </label>
    </>
  );
}

/** *Add a certificate* to somebody. */
export function AddCertificateDialog({
  people,
  busy,
  onSave,
  onClose,
}: {
  /** Active people, by name. */
  people: readonly { userId: string; name: string }[];
  busy: boolean;
  onSave: (
    userId: string,
    body: { name: string; neededToTreat: boolean; expiresOn: string | null },
  ) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [userId, setUserId] = useState("");
  const [name, setName] = useState("");
  const [neededToTreat, setNeeded] = useState(false);
  const [expiresOn, setExpires] = useState("");

  return (
    <Modal
      label="Add a certificate"
      onClose={onClose}
      panelClassName={styles.dialog}
      testId="add-certificate-dialog"
    >
      <div className={styles.dialogHead}>
        <div className={styles.dialogTitles}>
          <h2 className={styles.dialogTitle}>Add a certificate</h2>
        </div>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          Whose
          <RequiredMark />
        </span>
        <select
          className={styles.input}
          value={userId}
          disabled={busy}
          onChange={(event) => setUserId(event.target.value)}
          data-testid="certificate-person"
        >
          <option value="">Choose somebody</option>
          {people.map((row) => (
            <option key={row.userId} value={row.userId}>
              {row.name}
            </option>
          ))}
        </select>
      </label>

      <CertificateFields
        name={name}
        neededToTreat={neededToTreat}
        expiresOn={expiresOn}
        busy={busy}
        onName={setName}
        onNeeded={setNeeded}
        onExpires={setExpires}
      />

      <div className={styles.dialogFoot}>
        <button type="button" className={styles.ghost} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={busy || userId === "" || name.trim() === ""}
          onClick={() =>
            onSave(userId, {
              name: name.trim(),
              neededToTreat,
              expiresOn: expiresOn === "" ? null : expiresOn,
            })
          }
          data-testid="certificate-save"
        >
          Add the certificate
        </button>
      </div>
    </Modal>
  );
}

/**
 * Edit a certificate (HR-D17): rename, re-tick or re-date — a correction, so
 * the date may move either way — and *Delete*, which asks first.
 */
export function EditCertificateDialog({
  certificate,
  busy,
  onSave,
  onDelete,
  onClose,
}: {
  certificate: HrClinicCertificate;
  busy: boolean;
  onSave: (body: { name?: string; neededToTreat?: boolean; expiresOn?: string | null }) => void;
  onDelete: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(certificate.name);
  const [neededToTreat, setNeeded] = useState(certificate.neededToTreat);
  const [expiresOn, setExpires] = useState(certificate.expiresOn ?? "");
  const [deleting, setDeleting] = useState(false);

  if (deleting) {
    return (
      <ConfirmDialog
        title={`Delete ${certificate.name}?`}
        confirmLabel="Delete it"
        tone="danger"
        busy={busy}
        onConfirm={onDelete}
        onCancel={() => setDeleting(false)}
        testId="certificate-delete-confirm"
      >
        <p>
          {certificate.personName}&rsquo;s {certificate.name} goes, with every renewal recorded
          against it and their documents. This cannot be undone.
        </p>
      </ConfirmDialog>
    );
  }

  function save(): void {
    const body: { name?: string; neededToTreat?: boolean; expiresOn?: string | null } = {};
    if (name.trim() !== certificate.name) body.name = name.trim();
    if (neededToTreat !== certificate.neededToTreat) body.neededToTreat = neededToTreat;
    const nextExpiry = expiresOn === "" ? null : expiresOn;
    if (nextExpiry !== certificate.expiresOn) body.expiresOn = nextExpiry;
    onSave(body);
  }

  return (
    <Modal
      label={`Edit ${certificate.name}`}
      onClose={onClose}
      panelClassName={styles.dialog}
      testId="edit-certificate-dialog"
    >
      <div className={styles.dialogHead}>
        <div className={styles.dialogTitles}>
          <h2 className={styles.dialogTitle}>Edit {certificate.name}</h2>
          <p className={styles.dialogSub}>{certificate.personName}</p>
        </div>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>

      <CertificateFields
        name={name}
        neededToTreat={neededToTreat}
        expiresOn={expiresOn}
        busy={busy}
        onName={setName}
        onNeeded={setNeeded}
        onExpires={setExpires}
      />
      <p className={own.fieldNote}>
        A new date here corrects the one recorded. To renew it, use Renew on its row, which keeps
        the history.
      </p>

      <div className={styles.dialogFoot}>
        <button
          type="button"
          className={`${styles.quiet} ${own.footStart}`}
          disabled={busy}
          onClick={() => setDeleting(true)}
          data-testid="certificate-delete"
        >
          Delete
        </button>
        <button type="button" className={styles.ghost} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={busy || name.trim() === ""}
          onClick={save}
          data-testid="certificate-save"
        >
          Save
        </button>
      </div>
    </Modal>
  );
}

/**
 * *Check* a waiting renewal (HR-D4, HR-D12): the date the person typed,
 * editable, and the one confirmed is the one that takes effect — still later
 * than the date in force (HR-D10).
 */
export function CheckRenewalDialog({
  certificate,
  busy,
  onOpenFile,
  onCheck,
  onClose,
}: {
  certificate: HrClinicCertificate;
  busy: boolean;
  onOpenFile: (() => void) | null;
  onCheck: (expiresOn: string | undefined) => void;
  onClose: () => void;
}): React.JSX.Element {
  const typed = certificate.pendingRenewal?.expiresOn ?? null;
  const [expiresOn, setExpires] = useState(typed ?? "");
  const inForce = certificate.expiresOn;
  const problem =
    inForce === null
      ? null
      : expiresOn === ""
        ? "It needs its new expiry date."
        : expiresOn <= inForce
          ? `Later than ${shortDate(inForce)}, the date in force.`
          : null;

  return (
    <Modal
      label={`Check ${certificate.name}`}
      onClose={onClose}
      panelClassName={styles.dialog}
      testId="check-dialog"
    >
      <div className={styles.dialogHead}>
        <div className={styles.dialogTitles}>
          <h2 className={styles.dialogTitle}>Check {certificate.name}</h2>
          <p className={styles.dialogSub}>
            {certificate.personName} uploaded a renewal
            {typed === null ? "" : ` running to ${shortDate(typed)}`}
          </p>
        </div>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>New expiry</span>
        <input
          type="date"
          className={styles.input}
          value={expiresOn}
          min={inForce ?? undefined}
          disabled={busy}
          onChange={(event) => setExpires(event.target.value)}
          data-testid="check-expiry"
        />
        {problem !== null ? (
          <p className={own.fieldError} role="alert">
            {problem}
          </p>
        ) : (
          <p className={own.fieldNote}>
            Correct it if what they typed is not what the document says.
          </p>
        )}
      </label>

      {onOpenFile !== null && (
        <button
          type="button"
          className={styles.linkish}
          onClick={onOpenFile}
          data-testid="check-open-file"
        >
          Open the document
        </button>
      )}

      <p className={own.fieldNote}>
        Checking puts the new date in force. It cannot be taken back — a mistake is corrected by
        editing the certificate.
      </p>

      <div className={styles.dialogFoot}>
        <button type="button" className={styles.ghost} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={busy || problem !== null}
          onClick={() => onCheck(expiresOn === "" || expiresOn === typed ? undefined : expiresOn)}
          data-testid="check-save"
        >
          Check it
        </button>
      </div>
    </Modal>
  );
}

/** *Reject* a waiting renewal (HR-D11): the upload goes, and they are told why. */
export function RejectRenewalDialog({
  certificate,
  busy,
  onReject,
  onClose,
}: {
  certificate: HrClinicCertificate;
  busy: boolean;
  onReject: (reason: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [reason, setReason] = useState("");
  return (
    <ConfirmDialog
      title={`Reject ${certificate.personName}’s renewal?`}
      confirmLabel="Reject it"
      tone="danger"
      busy={busy}
      onConfirm={() => onReject(reason)}
      onCancel={onClose}
      testId="reject-dialog"
    >
      <p>
        The upload of {certificate.name} is deleted and its old date stands. They see that it was
        rejected, with your reason, until they upload again.
      </p>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Why — they will read this</span>
        <input
          type="text"
          className={styles.input}
          maxLength={500}
          value={reason}
          disabled={busy}
          placeholder="e.g. the scan is unreadable"
          onChange={(event) => setReason(event.target.value)}
          data-testid="reject-reason"
        />
      </label>
    </ConfirmDialog>
  );
}
